import type { ICardTransfer } from "@/modules/transaction/common/types";
import { Currencies } from "@/shared/common/enums";
import { Amount } from "@/shared/components";
import { formatCardNumber } from "@/shared/helpers";
import { CollapseGroup } from "@ab.uitools/ui-library/components/Collapse";
import { Copy } from "@ab.uitools/ui-library/components/Copy";
import { Divider } from "@ab.uitools/ui-library/components/Divider";
import { Text } from "@ab.uitools/ui-library/components/Text";
import { Tooltip } from "@ab.uitools/ui-library/components/Tooltip";
import { Positions } from "@ab.uitools/ui-library/components/Tooltip/types";
import type { RefObject } from "react";
import { useTranslation } from "react-i18next";

interface Props {
  transfer: ICardTransfer;
  scrollbarContainerRef?: RefObject<HTMLDivElement | null>;
}

export const CardTransferDetailRows = ({
  transfer,
  scrollbarContainerRef
}: Props) => {
  const { t } = useTranslation();

  return (
    <div>
      <div>
        <CollapseGroup
          className="detail-collapse"
          items={[
            {
              content: (
                <>
                  <div>
                    <div className="flexbox justify-content--between pt-20 pb-4 detail-row">
                      <Text
                        className="text-nowrap"
                        type="secondary"
                        size="medium"
                        weight="semibold">
                        {t("transfer.creditCardNumber")}
                      </Text>
                      <div className="flexbox align-items--center">
                        <Text size="medium" weight="bold">
                          {formatCardNumber({
                            card: `${transfer.creditCardNumber}`, isHidden: true
                          })}
                        </Text>
                      </div>
                    </div>
                    {transfer.beneficiary && (
                      <Divider
                        color="light"
                        type="primary"
                        className="mt-16"
                        isHorizontal
                      />
                    )}
                  </div>
                  {transfer.beneficiary && (
                    <div>
                      <div className="flexbox justify-content--between pt-20 pb-4 detail-row">
                        <Text
                          className="text-nowrap"
                          type="secondary"
                          size="medium"
                          weight="semibold">
                          {t("transfer.card.nameOnCard")}
                        </Text>
                        <Text size="medium" weight="bold">{transfer.beneficiary}</Text>
                      </div>
                    </div>
                  )}
                </>
              ),
              value: "beneficiary_details",
              isOpen: true,
              title: t("general.beneficiaryDetails")
            },
            {
              content: (
                <>
                  <div>
                    <div className="flexbox justify-content--between pt-20 pb-20 detail-row">
                      <Text
                        className="text-nowrap"
                        type="secondary"
                        size="medium"
                        weight="semibold">
                        {t("transfer.accountFrom")}
                      </Text>
                      <div className="flexbox align-items--center">
                        <Copy
                          textAfterCopy={t("general.copied")}
                          size="medium"
                          parentRef={scrollbarContainerRef as RefObject<HTMLDivElement>}
                          text={transfer.debitAccount?.number as string}
                        />
                        <Text size="medium" weight="bold">
                          {transfer.debitAccount?.number}
                        </Text>
                      </div>
                    </div>
                    <Divider
                      color="light"
                      type="primary"
                      isHorizontal
                    />
                  </div>
                  <div>
                    <div className="flexbox justify-content--between pt-20 pb-20 detail-row">
                      <Text
                        className="text-nowrap"
                        type="secondary"
                        size="medium"
                        weight="semibold">
                        {t("general.amount")}
                      </Text>
                      <Amount
                        currency={transfer.currency}
                        amount={transfer.amount as number}
                      />
                    </div>
                    <Divider
                      color="light"
                      type="primary"
                      isHorizontal
                    />
                  </div>
                  <div>
                    <div className="flexbox justify-content--between pt-20 pb-20 detail-row">
                      <Text
                        className="text-nowrap"
                        type="secondary"
                        size="medium"
                        weight="semibold">
                        {t("general.purpose")}
                      </Text>
                      <Tooltip
                        position={Positions.TOP_CENTER}
                        id="account-transfer-purpose-tooltip"
                        text={transfer.purpose}
                      />
                      <Text
                        size="medium"
                        weight="bold"
                        className="truncate cursor-pointer pl-16"
                        id="account-transfer-purpose-tooltip"
                      >
                        {transfer.purpose}
                      </Text>
                    </div>
                    <Divider
                      color="light"
                      type="primary"
                      isHorizontal
                    />
                  </div>
                  <div>
                    <div className="flexbox justify-content--between pt-20 pb-4 detail-row">
                      <Text
                        className="text-nowrap"
                        type="secondary"
                        size="medium"
                        weight="semibold">
                        {t("transfer.commissionFee")}
                      </Text>
                      <Amount
                        currency={Currencies.AMD}
                        amount={transfer.commissionAmount}
                      />
                    </div>
                    {transfer.commissionAccount && (
                      <Divider
                        color="light"
                        type="primary"
                        isHorizontal
                      />
                    )}
                  </div>
                  {transfer.commissionAccount?.number && (
                    <div>
                      <div className="flexbox justify-content--between pt-20 pb-20 detail-row">
                        <Text
                          className="text-nowrap"
                          type="secondary"
                          size="medium"
                          weight="semibold"
                        >
                          {t("transfer.feeFrom")}
                        </Text>
                        <div className="flexbox align-items--center">
                          <Copy
                            textAfterCopy={t("general.copied")}
                            size="medium"
                            parentRef={scrollbarContainerRef as RefObject<HTMLDivElement>}
                            text={transfer.commissionAccount?.number}
                          />
                          <Text size="medium" weight="bold">
                            {transfer.commissionAccount?.number}
                          </Text>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              ),
              value: "transfer_details",
              isOpen: true,
              title: t("transfer.transferDetails")
            },
            {
              content: (
                <>
                  <div>
                    <div className="flexbox justify-content--between pt-20 pb-20 detail-row">
                      <Text
                        className="text-nowrap"
                        type="secondary"
                        size="medium"
                        weight="semibold">
                        {t("transfer.transactionNumber")}
                      </Text>
                      <div className="flexbox align-items--center">
                        <Copy
                          textAfterCopy={t("general.copied")}
                          size="medium"
                          parentRef={scrollbarContainerRef as RefObject<HTMLDivElement>}
                          text={transfer.transactionNumber}
                        />
                        <Text size="medium" weight="bold">
                          {transfer.transactionNumber}
                        </Text>
                      </div>
                    </div>
                    <Divider
                      color="light"
                      type="primary"
                      isHorizontal
                    />
                  </div>
                  <div>
                    <div className="flexbox justify-content--between pt-20 pb-4 detail-row">
                      <Text
                        className="text-nowrap"
                        type="secondary"
                        size="medium"
                        weight="semibold"
                      >
                        {t("general.date")}
                      </Text>
                      <Text size="medium" weight="bold">
                        {transfer.scheduleDate}
                      </Text>
                    </div>
                  </div>
                </>
              ),
              value: "other_details",
              isOpen: true,
              title: t("general.otherDetails")
            },
          ]}
        />
      </div>
    </div>
  );
};
